import { Check, Info } from "lucide-react";
import { cn } from "@/lib/utils";
import { Plan, formatPrice } from "@/config/subscriptionPlans";
import { useTranslation } from "react-i18next";
import {
  PopoverRoot,
  PopoverTrigger,
  PopoverContent,
} from "@/components/primitives/popover";

interface PlanCardProps {
  plan: Plan;
  isYearly: boolean;
  isSelected: boolean;
  isCurrentPlan: boolean;
  isRecommended?: boolean;
  onClick?: () => void;
  isDisabled?: boolean;
}

export default function PlanCard({
  plan,
  isYearly,
  isSelected,
  isCurrentPlan,
  isRecommended = false,
  onClick,
  isDisabled = false,
}: PlanCardProps) {
  const { t } = useTranslation();
  const isPaid = plan.id !== "free";
  const displayPrice = isYearly ? plan.yearlyMonthlyEquiv : plan.monthlyPrice;
  const annualSavings = plan.annualSavings;

  const ribbonText = isCurrentPlan
    ? t("subscription.yourPlanRibbon")
    : isRecommended
      ? t("subscription.bestValueRibbon")
      : null;

  return (
    // Outer wrapper gives top-margin so the absolute ribbon doesn't clip
    <div
      className={cn(
        "relative pt-3",
        isRecommended && !isCurrentPlan ? "-translate-y-1.5" : "",
      )}
    >
      {/* Ribbon */}
      {ribbonText && (
        <span
          className={cn(
            "absolute left-4 top-0 z-10 rounded-full px-3 py-1 text-[10px] font-bold uppercase tracking-widest",
            isCurrentPlan
              ? "bg-foreground text-background"
              : "bg-secondary text-secondary-foreground",
          )}
        >
          {ribbonText}
        </span>
      )}

      {/* Card */}
      <div
        onClick={isDisabled ? undefined : onClick}
        role={isDisabled ? undefined : "button"}
        tabIndex={isDisabled ? undefined : 0}
        onKeyDown={
          isDisabled
            ? undefined
            : (e) => {
                if (e.key === "Enter" || e.key === " ") {
                  if (e.key === " ") {
                    e.preventDefault();
                  }
                  onClick?.();
                }
              }
        }
        className={cn(
          "relative flex h-full flex-col gap-4 rounded-xl border p-5 transition-all duration-150",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
          isDisabled ? "cursor-default" : "cursor-pointer",
          isRecommended && !isCurrentPlan
            ? "border-secondary/70 bg-gradient-to-b from-secondary/10 to-card shadow-[0_14px_32px_hsl(var(--secondary)/0.15)]"
            : isCurrentPlan
              ? "border-border bg-gradient-to-b from-foreground/5 to-card"
              : "border-border bg-card",
          isSelected && !isDisabled
            ? "border-secondary shadow-[0_0_0_1px_hsl(var(--secondary))_inset]"
            : !isDisabled && !isRecommended && !isCurrentPlan
              ? "hover:border-secondary/50"
              : "",
        )}
      >
        {/* Plan name + tagline */}
        <div>
          <h3 className="text-[17px] font-semibold leading-tight tracking-tight">
            {plan.name}
          </h3>
          <p className="mt-1 min-h-[2.4em] text-[12px] leading-[1.45] text-muted-foreground">
            {plan.tagline}
          </p>
        </div>

        {/* Price block */}
        <div className="flex flex-col gap-1">
          {isYearly && isPaid && (
            <span className="text-[11px] text-muted-foreground line-through">
              {formatPrice(plan.monthlyPrice)}
              {t("subscription.perMonth")}
            </span>
          )}
          <div className="flex items-baseline gap-1.5">
            <span className="text-[30px] font-bold tabular-nums leading-none tracking-tight">
              {formatPrice(displayPrice)}
            </span>
            {isPaid && (
              <span className="text-sm text-muted-foreground">
                {t("subscription.perMonth")}
              </span>
            )}
          </div>
          {isYearly && isPaid && annualSavings > 0 && (
            <span className="rounded self-start bg-green-500/15 px-1.5 py-0.5 text-[10.5px] font-semibold text-green-400">
              {t("subscription.saveAmountYr", { amount: annualSavings })}
            </span>
          )}
          <p className="text-[11px] tabular-nums text-muted-foreground">
            {isPaid
              ? isYearly
                ? t("subscription.billedYearlyAmount", {
                    amount: formatPrice(plan.yearlyPrice),
                  })
                : t("subscription.billedMonthlyShort")
              : "\u00a0"}
          </p>
        </div>

        {/* OA credits panel */}
        <div className="flex items-center gap-2.5 rounded-lg bg-muted px-3 py-2.5">
          <span className="flex size-[30px] shrink-0 items-center justify-center rounded-md bg-secondary text-[9px] font-bold tracking-wide text-secondary-foreground">
            OA
          </span>
          <div className="flex flex-col leading-tight">
            <span className="text-[9px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">
              {t("subscription.orionArchitectLabel")}
            </span>
            <span className="text-[13px] font-semibold tabular-nums">
              {plan.architectCredits}
            </span>
          </div>
          <PopoverRoot>
            <PopoverTrigger asChild>
              <button
                type="button"
                onClick={(e) => e.stopPropagation()}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.stopPropagation();
                  }
                }}
                className="ml-auto flex items-center text-muted-foreground/50 transition-colors hover:text-muted-foreground"
                aria-label={t("subscription.creditInfoAriaLabel")}
              >
                <Info size={13} />
              </button>
            </PopoverTrigger>
            <PopoverContent
              side="top"
              align="end"
              className="text-[11.5px] leading-relaxed"
            >
              <p className="mb-2 font-semibold text-foreground">
                {t("subscription.creditPopoverTitle")}
              </p>
              <p className="text-muted-foreground">
                {t("subscription.creditPopoverBody")}
              </p>
            </PopoverContent>
          </PopoverRoot>
        </div>

        {/* Feature bullets */}
        <ul className="flex flex-col gap-2">
          {plan.features.map((feature) => (
            <li key={feature} className="flex items-start gap-2">
              <span className="mt-0.5 flex size-[14px] shrink-0 items-center justify-center rounded-full bg-green-500/15">
                <Check size={9} strokeWidth={3} className="text-green-400" />
              </span>
              <span className="text-[12px] leading-[1.45] text-foreground/85">
                {feature}
              </span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
